import React, { useState } from 'react';
import { Copy, Check, ClipboardList } from 'lucide-react';
import { RATE_CARD_ITEMS } from '../lib/rateCard';
import { Input } from './ui/input';
import { toast } from 'sonner';

// Fixed rate card table with per-row (and copy-all) clipboard buttons, so a rate
// card / insights reply can be quickly assembled without retyping numbers.
const RateCardTable = ({ values = {}, editable = false, onChange, talentName }) => {
  const [copiedId, setCopiedId] = useState(null);
  const filled = RATE_CARD_ITEMS.filter((item) => (values[item.id] || '').trim());

  const flash = (id) => {
    setCopiedId(id);
    setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1500);
  };

  const copyRow = async (item, rate) => {
    try {
      await navigator.clipboard.writeText(`${item.label}: ${rate}`);
      flash(item.id);
      toast.success('copied to clipboard');
    } catch {
      toast.error('could not copy');
    }
  };

  const copyAll = async () => {
    if (!filled.length) return;
    const text = [talentName ? `${talentName} — rate card` : 'rate card', ...filled.map((item) => `${item.label}: ${values[item.id]}`)].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      flash('__all__');
      toast.success('full rate card copied');
    } catch {
      toast.error('could not copy');
    }
  };

  return (
    <div data-testid="rate-card-table">
      {!editable && (
        <div className="flex justify-end mb-1.5">
          <button
            type="button"
            onClick={copyAll}
            disabled={!filled.length}
            className="inline-flex items-center gap-1.5 text-[11px] font-medium text-pink-600 hover:text-pink-700 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="rate-card-copy-all"
          >
            {copiedId === '__all__' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <ClipboardList className="w-3.5 h-3.5" />} copy full rate card
          </button>
        </div>
      )}
      <div className="divide-y divide-slate-100">
        {RATE_CARD_ITEMS.map((item) => {
          const rate = values[item.id] || '';
          return (
            <div key={item.id} className="flex items-center gap-2 py-2" data-testid={`rate-card-row-${item.id}`}>
              <div className="flex-1 text-sm text-slate-700">{item.label}</div>
              {editable ? (
                <Input
                  value={rate}
                  onChange={(e) => onChange(item.id, e.target.value)}
                  placeholder="e.g. £500"
                  className="w-32 h-8 text-sm text-right"
                  data-testid={`rate-card-input-${item.id}`}
                />
              ) : (
                <>
                  <div className="w-32 text-sm text-right font-medium text-slate-900">
                    {rate || <span className="text-slate-300 italic">—</span>}
                  </div>
                  {rate && (
                    <button
                      type="button"
                      onClick={() => copyRow(item, rate)}
                      className="p-1.5 text-slate-400 hover:text-pink-600"
                      title="copy this rate"
                      data-testid={`rate-card-copy-${item.id}`}
                    >
                      {copiedId === item.id ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RateCardTable;
